/* eslint-disable no-console */
/**
 * _app.js 辅助函数
 */

const { TIMEOUT_CONFIG } = require('./constants');

/**
 * 带超时控制的 Promise 包装器
 * @param {Promise} promise - 原始 Promise
 * @param {number} timeout - 超时时间（毫秒）
 * @returns {Promise} 带超时控制的 Promise
 */
function withTimeout(promise, timeout = 5000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`CDN request timeout after ${timeout}ms`)), timeout)),
  ]);
}

/**
 * 判断是否为 CDN URL
 * @param {string} str - 字符串
 * @returns {boolean}
 */
function isValidUrl(str) {
  return /^(https?:\/\/).+\.(js|json)$/i.test(str);
}

/**
 * 清理空字符串的翻译词条，让 i18next 能正确 fallback 到英文
 * @param {object} translations - 翻译数据对象
 * @returns {object} 清理后的翻译数据
 */
function cleanEmptyTranslations(translations) {
  if (!translations || typeof translations !== 'object') {
    return translations;
  }

  const cleaned = {};

  Object.keys(translations).forEach((key) => {
    const value = translations[key];

    // 递归处理嵌套对象
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const cleanedNested = cleanEmptyTranslations(value);
      // 只有非空对象才保留
      if (Object.keys(cleanedNested).length > 0) {
        cleaned[key] = cleanedNested;
      }
    } else if (value !== '' && value !== null && value !== undefined) {
      // 只保留非空字符串的词条
      cleaned[key] = value;
    }
    // 空字符串、null、undefined 都会被过滤掉，让 i18next fallback
  });

  return cleaned;
}

/**
 * 处理 i18n 数据（从中间件注入的数据中请求 CDN 并解析）
 * @param {object} config - 配置对象
 * @param {object} config.i18nDataRaw - 中间件注入的原始数据
 * @param {object} config.manifest - 命名空间与 routeId 的映射
 * @param {Function} config.requestCdnData - 请求 CDN 数据的函数
 * @param {object} config.TranslationAjax - (可选) API 实例，如未提供则服务端自动加载
 * @param {boolean} config.disableCache - (可选) 是否禁用缓存，用于 ISR 场景确保多语言实时获取
 * @returns {Promise<object>} 处理后的 i18n 数据
 */
async function processI18nData(config) {
  const { i18nDataRaw, manifest, requestCdnData, disableCache = false } = config;
  let { TranslationAjax } = config;

  console.log(`[processI18nData] Starting with lang: ${i18nDataRaw.lang}, disableCache: ${disableCache}`);
  console.log('[processI18nData] i18nDataRaw.resources:', Object.keys(i18nDataRaw.resources));
  console.log('[processI18nData] TranslationAjax provided:', TranslationAjax);

  // 🔑 区分 undefined（未提供，需要自动加载）和 null（明确禁用英文 fallback）
  // - 如果 config 中没有 TranslationAjax 字段（undefined），则自动加载
  // - 如果 config 中有 TranslationAjax 字段但为 null，则明确禁用，不加载
  const shouldAutoLoad = !('TranslationAjax' in config);

  if (shouldAutoLoad && typeof window === 'undefined') {
    try {
      TranslationAjax = require('portal-common/server/routes/translation/ajax/index');
      console.log('[processI18nData] Auto-loaded TranslationAjax');
    } catch (error) {
      console.warn('[_app] Failed to load TranslationAjax:', error.message);
    }
  } else if (TranslationAjax === null) {
    console.log('[processI18nData] TranslationAjax explicitly disabled (null), skipping English fallback');
  }

  const i18nData = {
    resources: {},
    ns: i18nDataRaw.ns,
    lang: i18nDataRaw.lang,
  };

  // 处理每个语言的翻译数据
  const languages = Object.keys(i18nDataRaw.resources);
  console.log('[processI18nData] Processing languages:', languages);

  for (const currentLang of languages) {
    console.log(`[processI18nData] Processing language: ${currentLang}`);
    i18nData.resources[currentLang] = {};

    const langData = i18nDataRaw.resources[currentLang];
    console.log(`[processI18nData] ${currentLang} namespaces:`, Object.keys(langData));

    // 并行请求所有 CDN 数据
    const fetchTasks = Object.keys(langData).map(async (ns) => {
      const urlOrData = langData[ns];
      const routeId = manifest[ns]?.routeId;

      // 如果是 CDN URL，请求并解析
      if (isValidUrl(urlOrData) && routeId) {
        try {
          // 🔑 如果禁用缓存，添加时间戳参数破坏缓存
          let cdnUrl = urlOrData;
          if (disableCache) {
            const cacheBuster = `_t=${Date.now()}`;
            cdnUrl = urlOrData.includes('?') ? `${urlOrData}&${cacheBuster}` : `${urlOrData}?${cacheBuster}`;
          }

          console.log(`[processI18nData] Fetching CDN data for ${ns}/${currentLang}: ${cdnUrl}`);
          // 直接请求 CDN（依赖 CDN 自身的缓存机制，带超时）
          const cdnData = await withTimeout(requestCdnData(cdnUrl), TIMEOUT_CONFIG.CDN);
          const parsedData = JSON.parse(cdnData);
          console.log(
            `[processI18nData] Successfully parsed CDN data for ${ns}/${currentLang}, keys:`,
            Object.keys(parsedData).length,
          );

          return { ns, data: parsedData };
        } catch (error) {
          console.error(`[_app] Error fetching CDN data for ${ns}/${currentLang}:`, error);
          return { ns, data: {} };
        }
      }

      // 如果已经是数据对象，直接使用
      return { ns, data: urlOrData };
    });

    const results = await Promise.all(fetchTasks);

    // 组装数据并清理空字符串词条（让 i18next 能正确 fallback）
    results.forEach(({ ns, data }) => {
      i18nData.resources[currentLang][ns] = cleanEmptyTranslations(data);
    });
  }

  // 🔑 关键优化：如果当前语言不是英文，同时加载英文作为 fallback
  // 这样当某个词条在当前语言中不存在时，i18next 可以降级到英文
  if (i18nData.lang !== 'en' && TranslationAjax) {
    console.log(`[processI18nData] Loading English fallback for ${i18nData.lang}`);
    try {
      // 使用中间件的 fetchI18nData 逻辑加载英文词条
      const { fetchI18nData } = require('./middleware');

      i18nData.resources.en = {};

      const fallbackTasks = i18nData.ns.map(async (ns) => {
        const routeId = manifest[ns]?.routeId;
        if (!routeId) {
          return { ns, data: {} };
        }

        // 获取英文 CDN URL（根据 disableCache 参数决定是否跳过 URL 缓存）
        const urlOrData = await fetchI18nData(TranslationAjax, routeId, 'en', disableCache);

        // 如果是 URL，请求 CDN
        if (isValidUrl(urlOrData)) {
          try {
            // 🔑 如果禁用缓存，添加时间戳参数破坏缓存
            let cdnUrl = urlOrData;
            if (disableCache) {
              const cacheBuster = `_t=${Date.now()}`;
              cdnUrl = urlOrData.includes('?') ? `${urlOrData}&${cacheBuster}` : `${urlOrData}?${cacheBuster}`;
            }

            const cdnData = await withTimeout(requestCdnData(cdnUrl), TIMEOUT_CONFIG.CDN);
            const parsedData = JSON.parse(cdnData);
            return { ns, data: parsedData };
          } catch (error) {
            console.error(`[_app] Error fetching English fallback for ${ns}:`, error);
            return { ns, data: {} };
          }
        }

        return { ns, data: urlOrData };
      });

      const fallbackResults = await Promise.all(fallbackTasks);

      fallbackResults.forEach(({ ns, data }) => {
        i18nData.resources.en[ns] = data;
      });

      console.log('[processI18nData] English fallback loaded successfully');
    } catch (error) {
      console.error('[_app] Error loading English fallback:', error);
      // 降级：不影响主流程
    }
  }

  console.log('[processI18nData] Final i18nData.resources keys:', Object.keys(i18nData.resources));
  console.log('[processI18nData] Final i18nData.lang:', i18nData.lang);

  return i18nData;
}

module.exports = {
  withTimeout,
  isValidUrl,
  cleanEmptyTranslations,
  processI18nData,
};
